import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getOptionalLeoUuid,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { TemplateContent, TemplateContentDTO } from "./templateContent";
/* eslint-disable import/export */
export abstract class CreateEmailTemplateRPC implements
  LeoRPC<CreateEmailTemplateRPC.Request, CreateEmailTemplateRPC.Response, CreateEmailTemplateRPC.Errors.Errors>
{
  abstract execute(
    request: CreateEmailTemplateRPC.Request
  ): Promise<LeoRPCResult<CreateEmailTemplateRPC.Response, CreateEmailTemplateRPC.Errors.Errors>>;
}

export namespace CreateEmailTemplateRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    templateName: BoundedNameDTO;
    templateContent: TemplateContentDTO;
    projectId?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCreateEmailTemplateRPCRequest: boolean = true;
  
    readonly templateName: BoundedName;
    readonly templateContent: TemplateContent;
    readonly projectId: LeoUUID | null;
  
  
    constructor(
    templateName: BoundedName, 
    templateContent: TemplateContent, 
    projectId: LeoUUID | null = null
    ) {
      this.templateName = templateName;
      this.templateContent = templateContent;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const templateName = BoundedName.fromDTO(getObject(dto, "templateName", InvalidRequestTemplateNameError));
      const templateContent = TemplateContent.fromDTO(getObject(dto, "templateContent", InvalidRequestTemplateContentError));
      const projectId = getOptionalLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        templateName,
        templateContent,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          templateName: this.templateName.toDTO(),
          templateContent: this.templateContent.toDTO(),
      };
      if (this.projectId !== undefined && this.projectId !== null){
        dto.projectId = this.projectId.toDTO();
      }
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CreateEmailTemplateRPC(templateName='${this.templateName}',templateContent='${this.templateContent}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `CreateEmailTemplateRPC.Response()`;
  };

  export enum RPCError {
    DuplicateTemplateName = "DUPLICATE_TEMPLATE_NAME",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface DuplicateTemplateNameDTO {
      code: string;
    }
    
    export class DuplicateTemplateName extends LeoRPCError<RPCError> {
      readonly isDuplicateTemplateName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DuplicateTemplateName;
        super(code);
      }
    
    
      toDTO(): DuplicateTemplateNameDTO {
        const dto: DuplicateTemplateNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DuplicateTemplateName.prototype.toString = function toString(): string {
      return `CreateEmailTemplateRPC.DUPLICATE_TEMPLATE_NAME()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `CreateEmailTemplateRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `CreateEmailTemplateRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = DuplicateTemplateName | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = DuplicateTemplateNameDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.DuplicateTemplateName:
          return new DuplicateTemplateName();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestTemplateNameError extends CreateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTemplateNameError";
  }
}

export class InvalidRequestTemplateContentError extends CreateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestTemplateContentError";
  }
}

export class InvalidRequestProjectIdError extends CreateEmailTemplateRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}


export class InvalidResponseDuplicateTemplateNameError extends CreateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDuplicateTemplateNameError";
  }
}

export class InvalidResponseInvalidProjectIdError extends CreateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends CreateEmailTemplateRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

